/**
 * Sage contact details for entities (Farmer, Agent, Lab).
 * Maps to SageContactDetailsDto on the backend.
 */
export interface SageContactDetails {
  sageName?: string;
  taxReference?: string;
  postalAddress01?: string;
  postalAddress02?: string;
  postalAddress03?: string;
  postalAddress04?: string;
  postalAddress05?: string;
  deliveryAddress01?: string;
  deliveryAddress02?: string;
  deliveryAddress03?: string;
  deliveryAddress04?: string;
  deliveryAddress05?: string;
  isSageReady: boolean;
  isLinkedToSage: boolean;
  isCustomer: boolean;
  isSupplier: boolean;
}

/**
 * Write DTO for creating/updating Sage contact details.
 */
export interface UpsertSageContactDetails {
  sageName?: string;
  taxReference?: string;
  postalAddress01?: string;
  postalAddress02?: string;
  postalAddress03?: string;
  postalAddress04?: string;
  postalAddress05?: string;
  deliveryAddress01?: string;
  deliveryAddress02?: string;
  deliveryAddress03?: string;
  deliveryAddress04?: string;
  deliveryAddress05?: string;
}

/**
 * Derives a human-readable Sage link status label.
 *
 * Note: an entity that is "ready" only has the local Sage metadata filled in
 * (a Sage Name) — it does NOT yet exist in Sage. The label therefore reads
 * "Ready to create" rather than the old "Ready"/"Sage Ready", which wrongly
 * implied the Sage record already existed. Only a `isLinkedToSage` entity is
 * backed by a real Sage customer/supplier.
 */
export function getSageLinkLabel(sage?: SageContactDetails): string {
  if (!sage?.isLinkedToSage) {
    return sage?.isSageReady ? 'Ready to create' : 'Not set';
  }
  if (sage.isCustomer && sage.isSupplier) return 'Customer & Supplier';
  if (sage.isCustomer) return 'Customer';
  if (sage.isSupplier) return 'Supplier';
  return 'Linked';
}

/**
 * Returns a MUI color key based on Sage link status.
 * ready-but-not-created → warning; linked → success (both roles) / info (one role).
 */
export function getSageLinkColor(sage?: SageContactDetails): 'success' | 'info' | 'warning' | 'default' {
  if (!sage?.isLinkedToSage) {
    return sage?.isSageReady ? 'warning' : 'default';
  }
  if (sage.isCustomer && sage.isSupplier) return 'success';
  return 'info';
}

/**
 * Conflict data returned in the 409 response when a Sage supplier already exists.
 * Lives under `ApiResponse.errorContext` since the Sage middleware was folded into
 * the global exception middleware in PR2 (envelope: `{ errorCode, errorContext, ... }`).
 */
export interface SageSupplierConflictData {
  sageSupplierId: number;
  supplierName: string;
  entityType: string;
  entityId: number;
}

/**
 * Sage supplier details returned from the find-supplier endpoint.
 * Maps to SupplierDto on the backend.
 */
export interface SageSupplierDetails {
  id: number;
  name: string;
  contactName?: string;
  telephone?: string;
  mobile?: string;
  email?: string;
  taxReference?: string;
  active: boolean;
  balance: number;
  postalAddress01?: string;
  postalAddress02?: string;
  postalAddress03?: string;
  postalAddress04?: string;
  postalAddress05?: string;
  deliveryAddress01?: string;
  deliveryAddress02?: string;
  deliveryAddress03?: string;
  deliveryAddress04?: string;
  deliveryAddress05?: string;
}

/**
 * Request body for linking an existing Sage supplier.
 */
export interface LinkExistingSageSupplierRequest {
  sageSupplierId: number;
  syncDataToDris: boolean;
}

/**
 * Sage customer details returned from the find-customer endpoint.
 * Maps to CustomerDto on the backend.
 */
export interface SageCustomerDetails {
  id: number;
  name: string;
  contactName?: string;
  telephone?: string;
  mobile?: string;
  email?: string;
  taxReference?: string;
  active: boolean;
  balance: number;
  postalAddress01?: string;
  postalAddress02?: string;
  postalAddress03?: string;
  postalAddress04?: string;
  postalAddress05?: string;
  deliveryAddress01?: string;
  deliveryAddress02?: string;
  deliveryAddress03?: string;
  deliveryAddress04?: string;
  deliveryAddress05?: string;
}

/**
 * Request body for linking an existing Sage customer.
 */
export interface LinkExistingSageCustomerRequest {
  sageCustomerId: number;
  syncDataToDris: boolean;
}

/**
 * Active Sage entity link + sync telemetry, returned by the
 * `sage-entity-links/{entityType}/{entityId}` endpoints.
 * Maps to SageEntityLinkDto on the backend.
 */
export interface SageEntityLinkDto {
  id: number;
  entityType: string;
  entityId: number;
  /** Present when the entity is linked as a Sage customer. */
  sageCustomerId?: string;
  /** Present when the entity is linked as a Sage supplier. */
  sageSupplierId?: string;
  isActive: boolean;
  lastSyncedAt?: string;
  lastSyncAttemptAt?: string;
  /** 'Success' | 'Failed' when a sync has been attempted. */
  lastSyncStatus?: string;
  lastSyncError?: string;
}

/**
 * Conflict data returned in the 409 response when a Sage customer already exists.
 * Mirrors {@link SageSupplierConflictData}; lives under `ApiResponse.errorContext`.
 */
export interface SageCustomerConflictData {
  sageCustomerId: number;
  customerName: string;
  entityType: string;
  entityId: number;
}

/**
 * Detects a 409 "Sage Customer Exists" conflict.
 *
 * Handles BOTH error shapes so it works regardless of where it's caught:
 *   1. Post-PR2 normalized shape — the response interceptor turns any envelope
 *      carrying `errorCode` into a `NormalizedApiError` (`{ errorCode, errorContext,
 *      httpStatus }`, no `.response`). This is what `ensure-customer`'s 409 becomes.
 *   2. Legacy raw-AxiosError shape — `err.response.data.errorCode` + `errorContext`.
 *
 * Returns the conflict payload if found, null otherwise.
 */
export function isSageCustomerConflict(err: unknown): SageCustomerConflictData | null {
  if (typeof err !== 'object' || err === null) return null;

  // (1) Normalized envelope error (produced by the response interceptor).
  const normalized = err as {
    errorCode?: unknown;
    errorContext?: Record<string, unknown> | null;
    httpStatus?: number;
  };
  let status: number | undefined =
    typeof normalized.httpStatus === 'number' ? normalized.httpStatus : undefined;
  let errorCode: string | undefined =
    typeof normalized.errorCode === 'string' ? normalized.errorCode : undefined;
  let ctx: Record<string, unknown> | null | undefined = normalized.errorContext;

  // (2) Raw AxiosError fallback — only if we didn't already read an errorCode.
  if (!errorCode && 'response' in err) {
    const axiosErr = err as {
      response?: {
        status?: number;
        data?: { errorCode?: string; errorContext?: Record<string, unknown> };
      };
    };
    status = axiosErr.response?.status;
    errorCode = axiosErr.response?.data?.errorCode;
    ctx = axiosErr.response?.data?.errorContext;
  }

  if (status !== 409) return null;
  if (errorCode !== 'SAGE_CUSTOMER_EXISTS') return null;
  if (!ctx) return null;
  if (
    typeof ctx.sageCustomerId !== 'number' ||
    typeof ctx.customerName !== 'string' ||
    typeof ctx.entityType !== 'string' ||
    typeof ctx.entityId !== 'number'
  ) {
    return null;
  }
  return {
    sageCustomerId: ctx.sageCustomerId,
    customerName: ctx.customerName,
    entityType: ctx.entityType,
    entityId: ctx.entityId,
  };
}

/**
 * Builds the picker candidate for the Sage customer named in a 409 conflict, so
 * `SageLinkCustomerDialog` can pre-select it via `initialCandidate`.
 */
export function customerConflictCandidate(conflict: SageCustomerConflictData): SageCustomerDetails {
  return {
    id: conflict.sageCustomerId,
    name: conflict.customerName,
    active: true,
    balance: 0,
  };
}

/**
 * A single field that differs between the DRIS entity and its linked Sage record.
 * Only drifting fields are returned by the backend — matching fields are omitted.
 */
export interface SageDriftField {
  field: string;
  drisValue?: string;
  sageValue?: string;
}

/**
 * Result of the pre-generation Sage drift check for a billed party.
 * Maps to SageDriftDto on the backend.
 *
 * The check NEVER throws on a Sage read error — it returns `checkPerformed: false`
 * instead, so document generation is never blocked by a transient Sage failure.
 */
export interface SageDriftDto {
  /** True when the entity is linked to a Sage customer/supplier. */
  isLinked: boolean;
  /** True when the Sage record was actually read and compared (false on a Sage error). */
  checkPerformed: boolean;
  /** True when at least one field differs between DRIS and Sage. */
  hasDrift: boolean;
  entityName: string;
  /** Whether the entity is linked as a Sage Customer (Farmer/Agent) or Supplier (Lab). */
  linkKind: 'Customer' | 'Supplier';
  /** Only the fields that differ. Empty when there is no drift. */
  fields: SageDriftField[];
}

/**
 * Reconcile direction for {@link sageCustomerLinkingService.reconcile}.
 * - PushToSage: update the Sage record from DRIS ("Use DRIS values").
 * - PullToDris: update the DRIS entity from Sage ("Use Sage values").
 */
export type SageReconcileDirection = 'PushToSage' | 'PullToDris';

/**
 * Detects a 409 "Sage Supplier Exists" conflict from an AxiosError.
 * Returns the conflict payload if found, null otherwise.
 *
 * Error path (post-PR2 envelope): AxiosError.response.data = ApiResponse body,
 * with `errorCode === 'SAGE_SUPPLIER_EXISTS'` and supplier metadata under
 * `errorContext`.
 */
export function isSageSupplierConflict(err: unknown): SageSupplierConflictData | null {
  if (typeof err !== 'object' || err === null || !('response' in err)) return null;
  const axiosErr = err as {
    response?: {
      status?: number;
      data?: { errorCode?: string; errorContext?: Record<string, unknown> };
    };
  };
  if (axiosErr.response?.status !== 409) return null;
  const body = axiosErr.response?.data;
  if (body?.errorCode !== 'SAGE_SUPPLIER_EXISTS') return null;
  const ctx = body.errorContext;
  if (!ctx) return null;
  if (
    typeof ctx.sageSupplierId !== 'number' ||
    typeof ctx.supplierName !== 'string' ||
    typeof ctx.entityType !== 'string' ||
    typeof ctx.entityId !== 'number'
  ) {
    return null;
  }
  return {
    sageSupplierId: ctx.sageSupplierId,
    supplierName: ctx.supplierName,
    entityType: ctx.entityType,
    entityId: ctx.entityId,
  };
}

/** A Sage Sales Representative (GET /api/sage/sales-reps). */
export interface SageSalesRep {
  id: number;
  firstName: string;
  lastName: string;
  name?: string | null;
  email?: string | null;
  mobile?: string | null;
  telephone?: string | null;
  active: boolean;
}

/** An agent's link to a Sage Sales Representative (DRI-85). `sageSalesRepId` null = not linked. */
export interface AgentSalesRepLink {
  agentId: number;
  sageSalesRepId?: number | null;
  salesRepName?: string | null;
  salesRepEmail?: string | null;
  salesRepActive?: boolean | null;
}

/** The existing rep returned (in errorContext) with a 409 SALES_REP_EXISTS from create. */
export interface SalesRepCandidate {
  id: number;
  name: string;
  email: string;
  active: boolean;
  matchedOn: 'Email' | 'Name' | string;
}
